/**
 * EMSLayout.tsx
 *
 * Main application layout for EMS using Grok-style design.
 *
 * Features:
 * - Desktop AdminRail navigation (collapsible)
 * - Mobile hamburger menu navigation
 * - Classification banners (top/bottom when module enabled)
 * - Toast notification container
 * - Responsive content area with proper margins
 * - LayoutContext for child component access
 *
 * Layout Structure:
 * +-----------------------------------------------------------+
 * | Classification Banner (Top - fixed, conditional)          |
 * +------------------+----------------------------------------+
 * |                  |                                        |
 * |    AdminRail     |            Main Content                |
 * |    (Desktop)     |              (Outlet)                  |
 * |                  |                                        |
 * +------------------+----------------------------------------+
 * | Classification Banner (Bottom - fixed, conditional)       |
 * +-----------------------------------------------------------+
 * | MobileNav (Mobile only - overlay)                         |
 * +-----------------------------------------------------------+
 * | ToastContainer (Fixed, bottom-right)                      |
 * +-----------------------------------------------------------+
 */

import { useState, useCallback, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminRail } from '@/base/components/layout/AdminRail';
import { MobileNav } from '@/base/components/layout/MobileNav';
import { ToastContainer } from '@/base/components/layout/ToastContainer';
import { ClassificationBanner } from '@/base/components/layout/ClassificationBanner';
import { CommandPalette } from '@/base/components/CommandPalette';
import { QuickAccessHandle } from '@/base/components/quick-access';
// EmergencyLockButton moved to AdminRail sidebar
import { PAGE_ACTIONS_PORTAL_ID } from '@/base/components/layout/PageActions';
import { VaultUnlockModal } from '@/base/components/vault/VaultUnlockModal';
import { LayoutProvider, useLayout } from '@/base/contexts/LayoutContext';
import { VaultProvider, useVault } from '@/base/contexts/VaultContext';
import { UpdateNotification } from '@/base/components/UpdateNotification';
import { useAuthStore } from '@/base/stores/authStore';
import { getSetupStatus } from '@/base/utils/api';

// ============================================================================
// Constants
// ============================================================================

const COLLAPSED_STORAGE_KEY = 'ems-adminrail-collapsed';

// ============================================================================
// Internal Layout Component
// ============================================================================

function EMSLayoutInner() {
  const location = useLocation();
  const { isLoading, isAuthenticated } = useAuthStore();
  const { isRailCollapsed } = useLayout();

  // Check if setup is needed (no admin exists)
  const { data: setupStatus, isLoading: isSetupLoading } = useQuery({
    queryKey: ['setup-status'],
    queryFn: getSetupStatus,
    staleTime: 30 * 1000,
    retry: false,
  });

  // Mobile navigation state
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Sync collapsed state from localStorage
  const [localCollapsed, setLocalCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      setLocalCollapsed(stored === 'true');
    };

    const interval = setInterval(handleStorageChange, 100);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Close mobile nav on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMobileNavOpen) {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileNavOpen]);

  // Mobile nav handlers
  const handleOpenMobileNav = useCallback(() => setIsMobileNavOpen(true), []);
  const handleCloseMobileNav = useCallback(() => setIsMobileNavOpen(false), []);

  // Show loading state
  if (isLoading || isSetupLoading) {
    return (
      <div className="min-h-screen bg-[var(--grok-void)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[var(--grok-text-muted)] border-t-[var(--ems-primary)] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--grok-text-muted)]">Loading EMS...</p>
        </div>
      </div>
    );
  }

  // Redirect to setup if no admin exists
  if (setupStatus?.needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectUrl}`} replace />;
  }

  // Determine main content margin based on rail state
  const effectiveCollapsed = isRailCollapsed ?? localCollapsed;
  const mainMarginClass = effectiveCollapsed ? 'lg:ml-16' : 'lg:ml-64';

  return (
    <VaultProvider>
      <EMSLayoutAuthenticated
        mainMarginClass={mainMarginClass}
        isMobileNavOpen={isMobileNavOpen}
        onOpenMobileNav={handleOpenMobileNav}
        onCloseMobileNav={handleCloseMobileNav}
      />
    </VaultProvider>
  );
}

// ============================================================================
// Authenticated Layout (wrapped in VaultProvider)
// ============================================================================

interface AuthenticatedLayoutProps {
  mainMarginClass: string;
  isMobileNavOpen: boolean;
  onOpenMobileNav: () => void;
  onCloseMobileNav: () => void;
}

function EMSLayoutAuthenticated({
  mainMarginClass,
  isMobileNavOpen,
  onOpenMobileNav,
  onCloseMobileNav,
}: AuthenticatedLayoutProps) {
  const vault = useVault();

  return (
    <div className="min-h-screen bg-[var(--grok-void)]">
      {/* Classification Banner - Top */}
      <ClassificationBanner position="top" />

      {/* Quick Access Handle */}
      <QuickAccessHandle />

      {/* Desktop Admin Rail */}
      <AdminRail />

      {/* Mobile Navigation */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onOpen={onOpenMobileNav}
        onClose={onCloseMobileNav}
      />

      {/* Main Content Area */}
      <main
        className={`
          min-h-screen
          transition-[margin-left] duration-200 ease-out
          ${mainMarginClass}
          pt-4 lg:pt-0
        `}
        role="main"
      >
        {/* Page Actions Portal - pages inject their actions here */}
        <div className="sticky top-0 z-40 flex items-center justify-end gap-2 px-6 py-2 bg-[var(--grok-void)]">
          <div id={PAGE_ACTIONS_PORTAL_ID} className="flex items-center gap-2" />
        </div>

        <UpdateNotification />
        <Outlet />
      </main>

      {/* Classification Banner - Bottom */}
      <ClassificationBanner position="bottom" />

      {/* Toast Notifications */}
      <ToastContainer />

      {/* Command Palette (Cmd+K / Ctrl+K) */}
      <CommandPalette />

      {/* Vault Unlock/Setup Modal (post-login prompt + emergency unlock) */}
      <VaultUnlockModal
        isOpen={vault.showUnlockModal}
        onClose={vault.dismissUnlockModal}
        isSetup={vault.isSetup}
        onSuccess={vault.refetchStatus}
      />
    </div>
  );
}

// ============================================================================
// Main Export
// ============================================================================

export function EMSLayout() {
  return (
    <LayoutProvider>
      <EMSLayoutInner />
    </LayoutProvider>
  );
}

export default EMSLayout;
